import type { Quaternion, Vector3 } from '@babylonjs/core/Maths/math.vector';

import { quatToArray, vec3ToArray } from './packing';
import type { RoomHub } from './roomHub';
import type { AvatarPoseUpdate, PoseDto } from './wire';

/**
 * Rate-limits the three high-frequency messages.
 *
 * Sampling the latest state on a timer, rather than sending on every change, is the whole
 * point: a render loop runs at 72-90 Hz and these messages fan out to every other peer.
 * Intermediate poses have no value once a newer one exists, so they are dropped rather
 * than queued — a late position is worse than no position. Stroke points are the one
 * exception to "intermediate has no value": they are batched rather than dropped, because
 * unlike a pose in transit, every point between two flushes is part of the line, not a
 * stale sample of the same thing.
 */

/** Held nodes move fast and are watched closely, so they get the higher rate. */
const NODE_HZ = 20;

/** Heads and hands are smoothed on arrival, so a lower rate is not visible. */
const AVATAR_HZ = 15;

/**
 * A stroke in progress is watched at least as closely as a held node, and the project has
 * directed this feature specifically to favour fidelity over wire economy — see the
 * shipped backend's own remarks on `StrokePointUpdate` and
 * `StreamThrottle.StrokePointMinIntervalMs` (Tunet.Api), which size their per-channel
 * backstop against this exact ~40 Hz target rather than the other two channels' 20 Hz.
 * plan/whiteboard.md's own prose still says "NODE_HZ (20 Hz)" for this — that line predates
 * the pivot recorded in the backend's own comments and was never corrected to match; this
 * follows the shipped, reasoned target instead of the stale prose. See the report for this
 * milestone for the full note.
 */
const STROKE_HZ = 40;

/** Below this much movement there is nothing worth telling anyone about. */
const POSITION_EPSILON = 0.002;
const ROTATION_EPSILON = 0.0015;

interface NodeSample {
  nodeId: string;
  p: number[];
  r: number[];
  s: number;
}

/** Points captured for one stroke, not yet flushed. */
interface StrokeBuffer {
  strokeId: string;
  points: number[][];
}

export class TransformStreamer {
  private nodeSample: NodeSample | null = null;
  private avatarSample: Omit<AvatarPoseUpdate, 't' | 'peerId'> | null = null;
  private strokeBuffer: StrokeBuffer | null = null;

  private lastNodeSentAt = 0;
  private lastAvatarSentAt = 0;
  private lastStrokeSentAt = 0;

  private lastSentNode: NodeSample | null = null;

  constructor(private readonly hub: RoomHub) {}

  /** Called every frame while a node is held. Cheap: it only records. */
  sampleNode(nodeId: string, position: Vector3, rotation: Quaternion, scale: number): void {
    this.nodeSample = {
      nodeId,
      p: vec3ToArray(position),
      r: quatToArray(rotation),
      s: scale,
    };
  }

  /** Stops streaming. Called on release, so no stale pose trails the commit. */
  clearNode(): void {
    this.nodeSample = null;
    this.lastSentNode = null;
  }

  sampleAvatar(head: PoseDto, left: PoseDto | null, right: PoseDto | null, leftPinch: boolean, rightPinch: boolean): void {
    this.avatarSample = { head, leftHand: left, rightHand: right, leftPinch, rightPinch };
  }

  /**
   * Called once per point a stroke's own capture (DrawingManager) decides is worth
   * recording — cheap: like sampleNode, it only buffers. Starting a *different* stroke id
   * resets the buffer, so a leftover point from a stroke that just ended can never ride
   * along with the next one's first flush.
   */
  sampleStrokePoint(strokeId: string, point: Vector3): void {
    if (this.strokeBuffer === null || this.strokeBuffer.strokeId !== strokeId) {
      this.strokeBuffer = { strokeId, points: [] };
    }

    this.strokeBuffer.points.push(vec3ToArray(point));
  }

  /** Stops streaming a stroke. Called when one ends — committed or abandoned — so no
   *  stale points trail behind it onto whatever gets drawn next. */
  clearStroke(): void {
    this.strokeBuffer = null;
  }

  /** Called once per frame. Emits at most one message per channel per interval. */
  flush(nowMs: number): void {
    this.flushNode(nowMs);
    this.flushAvatar(nowMs);
    this.flushStroke(nowMs);
  }

  private flushNode(nowMs: number): void {
    const sample = this.nodeSample;

    if (sample === null || nowMs - this.lastNodeSentAt < 1000 / NODE_HZ) {
      return;
    }

    // A node held perfectly still still needs no traffic; the holder's own client already
    // shows it where it is.
    if (this.lastSentNode !== null && !hasMoved(sample, this.lastSentNode)) {
      return;
    }

    this.lastNodeSentAt = nowMs;
    this.lastSentNode = sample;

    this.hub.streamNodeTransform({
      nodeId: sample.nodeId,
      p: sample.p,
      r: sample.r,
      s: sample.s,
      t: nowMs,
    });
  }

  private flushAvatar(nowMs: number): void {
    const sample = this.avatarSample;

    if (sample === null || nowMs - this.lastAvatarSentAt < 1000 / AVATAR_HZ) {
      return;
    }

    this.lastAvatarSentAt = nowMs;

    // Sent unconditionally rather than epsilon-gated: presence reads as alive because heads
    // never sit perfectly still, and a peer who stops transmitting looks disconnected.
    this.hub.streamAvatarPose({ ...sample, t: nowMs });
  }

  private flushStroke(nowMs: number): void {
    const buffer = this.strokeBuffer;

    // Nothing buffered is the common case: the pen is not moving, or not touching at all.
    // No epsilon check needed here the way flushNode has one — sampleStrokePoint is only
    // ever called with a point DrawingManager already decided was worth recording.
    if (buffer === null || buffer.points.length === 0) {
      return;
    }

    if (nowMs - this.lastStrokeSentAt < 1000 / STROKE_HZ) {
      return;
    }

    this.lastStrokeSentAt = nowMs;

    // Cleared, not replaced: keeping the same buffer object (with its strokeId) means a
    // point captured in the next frame, before this stroke ends, keeps accumulating under
    // the same id rather than silently starting a new buffer.
    const points = buffer.points;
    buffer.points = [];

    this.hub.streamStrokePoint({ strokeId: buffer.strokeId, points, t: nowMs });
  }
}

function hasMoved(a: NodeSample, b: NodeSample): boolean {
  for (let i = 0; i < 3; i += 1) {
    if (Math.abs((a.p[i] ?? 0) - (b.p[i] ?? 0)) > POSITION_EPSILON) {
      return true;
    }
  }

  for (let i = 0; i < 4; i += 1) {
    if (Math.abs((a.r[i] ?? 0) - (b.r[i] ?? 0)) > ROTATION_EPSILON) {
      return true;
    }
  }

  return Math.abs(a.s - b.s) > POSITION_EPSILON;
}
